import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Link,
  IconButton,
} from '@mui/material';
import {
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  YouTube,
  MusicNote, // Using MusicNote for TikTok as explicit TikTok icon might be missing or different version
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { ContactInfo } from '../../types';

const Footer: React.FC = () => {
  const [contact, setContact] = useState<ContactInfo | null>(null);

  useEffect(() => {
    const fetchContact = async () => {
      try {
        const contacts = await apiService.getContactInfo();
        if (contacts.length > 0) {
          setContact(contacts[0]);
        }
      } catch (error) {
        console.error('Failed to fetch contact info:', error);
      }
    };
    fetchContact();
  }, []);

  return (
    <Box
      component="footer"
      sx={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        color: 'white',
        pt: 8,
        pb: 4,
        mt: 'auto',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 6, mb: 6 }}>
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              Kursu Timor
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, opacity: 0.8, lineHeight: 1.6 }}>
              To make quality and practical education accessible for everyone and help learners build a better future.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {contact?.facebook_url && (
                <IconButton
                  component="a"
                  href={contact.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'white', background: 'rgba(255,255,255,0.1)', '&:hover': { background: 'rgba(255,255,255,0.2)', transform: 'translateY(-2px)' }, transition: 'all 0.3s ease' }}
                >
                  <Facebook />
                </IconButton>
              )}
              {contact?.twitter_url && (
                <IconButton
                  component="a"
                  href={contact.twitter_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'white', background: 'rgba(255,255,255,0.1)', '&:hover': { background: 'rgba(255,255,255,0.2)', transform: 'translateY(-2px)' }, transition: 'all 0.3s ease' }}
                >
                  <Twitter />
                </IconButton>
              )}
              {contact?.instagram_url && (
                <IconButton
                  component="a"
                  href={contact.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'white', background: 'rgba(255,255,255,0.1)', '&:hover': { background: 'rgba(255,255,255,0.2)', transform: 'translateY(-2px)' }, transition: 'all 0.3s ease' }}
                >
                  <Instagram />
                </IconButton>
              )}
              {contact?.linkedin && (
                <IconButton
                  component="a"
                  href={contact.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'white', background: 'rgba(255,255,255,0.1)', '&:hover': { background: 'rgba(255,255,255,0.2)', transform: 'translateY(-2px)' }, transition: 'all 0.3s ease' }}
                >
                  <LinkedIn />
                </IconButton>
              )}
              {contact?.youtube_url && (
                <IconButton
                  component="a"
                  href={contact.youtube_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'white', background: 'rgba(255,255,255,0.1)', '&:hover': { background: 'rgba(255,255,255,0.2)', transform: 'translateY(-2px)' }, transition: 'all 0.3s ease' }}
                >
                  <YouTube />
                </IconButton>
              )}
              {contact?.tiktok_url && (
                <IconButton
                  component="a"
                  href={contact.tiktok_url}
                  target="_blank"
                  rel="noopener noreferrer" // TikTok
                  sx={{ color: 'white', background: 'rgba(255,255,255,0.1)', '&:hover': { background: 'rgba(255,255,255,0.2)', transform: 'translateY(-2px)' }, transition: 'all 0.3s ease' }}
                >
                  <MusicNote /> 
                </IconButton>
              )}
            </Box>
          </Box>

          <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Quick Links
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[
                { text: 'All Courses', href: '/courses' },
                { text: 'Our Mentors', href: '/mentors' },
                { text: 'About Us', href: '/about' },
              ].map((link) => (
                <Link
                  key={link.text}
                  component={RouterLink}
                  to={link.href}
                  sx={{
                    color: 'rgba(255,255,255,0.8)',
                    textDecoration: 'none',
                    '&:hover': {
                      color: '#10b981',
                      textDecoration: 'underline',
                    },
                    transition: 'color 0.3s ease',
                  }}
                >
                  {link.text}
                </Link>
              ))}
            </Box>
          </Box>

          <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Contact Info
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                📧 {contact?.email || 'info@monteacademy.com'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                📞 {contact?.phone_number || '+1 (555) 123-4567'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                📍 {contact?.address || '123 Education St, Learning City, LC 12345'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            borderTop: '1px solid rgba(255,255,255,0.1)',
            pt: 4,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" sx={{ opacity: 0.6 }}>
            © 2026 Kursu Timor. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;